import { objectMap } from '@direct-frontend/stdlib';

import { getFormStoreSingleton } from '../store/store';
import { BaseValidationError } from '../types';
import { BaseFields } from '../fields/types';

import { assertFormInitialized } from './utils/assertFormInitialized';
import { assertFormNotSubmitting } from './utils/assertFormNotSubmitting';
import { getOrCreateFrozenInstance } from './frozenInstancesContext';
import {
    $internals,
    AsyncActionsFormApi,
    BoundFormAsyncActions,
    FormActionsMap,
    FormAsyncActionsMap,
    FormInstance,
} from './types';

/** Привязывает асинхронные экшены формы с поддержкой takeLatest */
export function bindFormAsyncActions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions extends FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions>,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    asyncActions: FormAsyncActions | undefined,
    /**
     * Собирает усечённый formApi для передачи в экшен на основе замороженного инстанса формы.
     * Конструируется в createForm, где доступны поля и getFieldInstance.
     */
    buildFormApi: (
        frozenForm: FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >
    ) => AsyncActionsFormApi<FormFields, FormValidationError, FormContext, FormActions>
): BoundFormAsyncActions<FormAsyncActions> {
    if (!asyncActions) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- кейс с отсутствующим asyncActions
        return {} as BoundFormAsyncActions<FormAsyncActions>;
    }

    const { updateFormAsyncActionsMeta, getFormAsyncActionsMeta } = getFormStoreSingleton(form.name);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- objectMap не выведет правильно тип BoundFormAsyncActions
    return objectMap(asyncActions, (asyncAction, actionName) => (...args: unknown[]) => {
        const frozenFormInitializationId = form[$internals].formInitializationId;
        const frozenForm = getOrCreateFrozenInstance(frozenFormInitializationId, form);

        assertFormInitialized({
            formName: form.name,
            formId: form.id,
            formInitializationId: frozenFormInitializationId,
        });

        // Разрешаем запуск в фазе ожидания сабмита (когда submit ждёт завершения экшенов),
        // но блокируем после её завершения
        assertFormNotSubmitting(
            `Can not run form async action "${String(actionName)}", while form is submitting`,
            {
                formName: form.name,
                formId: form.id,
                formInitializationId: frozenFormInitializationId,
            },
            undefined
        );

        const controller = new AbortController();

        // takeLatest: если экшен с таким именем уже запущен, абортим его
        const existingMeta = getFormAsyncActionsMeta({ formInitializationId: frozenFormInitializationId });
        if (existingMeta?.[actionName]) {
            existingMeta[actionName].controller.abort();
        }

        const actionPromise = asyncAction(buildFormApi(frozenForm), controller.signal, ...args).finally(() => {
            const currentMeta = getFormAsyncActionsMeta({ formInitializationId: frozenFormInitializationId });
            if (currentMeta?.[actionName]?.promise === actionPromise) {
                const newMeta = { ...currentMeta };
                delete newMeta[actionName];
                updateFormAsyncActionsMeta({
                    formInitializationId: frozenFormInitializationId,
                    asyncActionsMeta: newMeta,
                });
            }
        });

        updateFormAsyncActionsMeta({
            formInitializationId: frozenFormInitializationId,
            asyncActionsMeta: {
                ...existingMeta,
                [actionName]: { controller, promise: actionPromise },
            },
        });

        return actionPromise;
    }) as BoundFormAsyncActions<FormAsyncActions>;
}
